import { computed, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import PQueue from 'p-queue';

import { usePaymentParcel } from './usePayment';
import dayjs from '@/utils/dayjs';
import { useClient } from '@/lib/_API/useClient';
import { useCharge } from '@/lib/_API/useCharge';
import cloneDeep from 'lodash/cloneDeep';

/**
 *
 * Outbound - Parcel(택배)에서 사용되는 공통 state, methods 입니다.
 *
 * 주문접수/결제 단계 까지 같이 사용 됩니다.
 *
 */
export default function useParcel() {
  return {
    ...useParcelState(),
    ...useParcelUpdate(),
    ...useParcelAction(),
    ...useParcelGetters(),
  };
}
/**
 * 출/도착지 데이터 모델
 */
export const MODEL_PIN = (): outbound.pin => {
  return cloneDeep({
    name: '',
    phoneNumber: {
      value: '',
      isValidate: null,
    },
    jibunAddress: '',
    roadAddress: '',
    detailAddress: '',
    dongName: '',
    postalCode: '',
    latitude: 0,
    longitude: 0,
    pickupTime: '',
  });
};
/**
 * 물품정보 데이터 모델
 */
export const MODEL_ITEM = (): outbound.parcel.item => {
  return cloneDeep({
    category: '',
    price: 0,
    size: {
      displayName: '',
      _id: '',
    },
  });
};
/**
 * 요금정보 데이터 모델
 */
export const MODEL_FEE = (): outbound.parcel.fee => {
  return cloneDeep({
    totalCharge: 0,
    baseCharge: 0,
    zoneCharge: 0,
    ferryCharge: 0,
    chargeUuid: '',
  });
};
/**
 * 주문정보 데이터 모델
 */
export const MODEL_ORDERINFOMATION = (): outbound.parcel.orderInfomation => {
  return cloneDeep({
    checked: true, // default true (주문선택)
    policyItem: false,
    clientComment: '',
    pickupFromCenter: true, // 지점출발여부 (true === '지점출발')
    customFields: [],
    customerDropOffMethod: '',
    onsitePickupRequestedAt: undefined,
    onsitePickupClientComment: undefined,
  });
};
/**
 * Package 데이터 모델
 */
export const MODEL_PACKAGE = (): outbound.parcel.package => {
  return cloneDeep({
    origin: MODEL_PIN(),
    destination: MODEL_PIN(),
    item: MODEL_ITEM(),
    fee: MODEL_FEE(),
    orderInfomation: MODEL_ORDERINFOMATION(),
    customerDropOffMethod: '',
    onsitePickupRequestedAt: undefined,
    onsitePickupClientComment: undefined,
  });
};

/**
 *
 *
 * 택배 상태 값을 읽습니다.
 *
 *
 */
export function useParcelState() {
  const store = useStore();
  const stateParcel = store.state.outboundParcel;

  /**
   * 작성중인 출발지 정보
   */
  const parcelOrigin = computed<Readonly<outbound.pin>>(() => stateParcel.packageOrigin);
  /**
   * 작성중인 도착지 정보
   */
  const parcelDestination = computed<Readonly<outbound.pin>>(() => stateParcel.packageDestination);
  /**
   * 작성중인 물품 정보
   */
  const parcelItem = computed<Readonly<outbound.parcel.item>>(() => stateParcel.packageItem);
  /**
   * 작성중인 요금 정보
   */
  const parcelFee = computed<Readonly<outbound.parcel.fee>>(() => stateParcel.packageFee);
  /**
   * 요금 계산 중인지 정보
   */
  const isCalculating = computed<boolean>(() => parcelFee.value.totalCharge === null);
  /**
   * 작성중인 주문정보
   */
  const parcelOrderInfomation = computed<Readonly<outbound.parcel.orderInfomation>>(
    () => stateParcel.packageOrderInfomation
  );

  /**
   * 작성 완료된 주문 정보
   */
  const parcelCompleteList = computed<Readonly<outbound.parcel.package[]>>(() => stateParcel.completeList);

  //필수 입력 항목 체크 팝업 표시 여부
  // const isValidateData = computed<boolean>(() => stateParcel.validatePopup);

  //접수가능 시간 안내 팝업 표시 여부
  const receptionTimePopup = computed<boolean>(() => stateParcel.receptionTimePopup);
  return {
    parcelOrigin,
    parcelDestination,
    parcelItem,
    parcelFee,
    parcelOrderInfomation,
    parcelCompleteList,
    isCalculating,
    receptionTimePopup,
  };
}
/**
 *
 *
 * 택배 상태 값을 단순 업데이트 합니다.
 *
 *
 */
export function useParcelUpdate() {
  const store = useStore();

  /**
   * 출발지 정보 업데이트
   */
  const updateOrigin = <T extends outbound.pin, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundParcel/updatePackageOrigin', {
      key: key,
      value: value,
    });
  };
  /**
   * 도착지 정보 업데이트
   */
  const updateDestination = <T extends outbound.pin, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundParcel/updatePackageDestination', {
      key: key,
      value: value,
    });
  };
  /**
   * 물품정보 업데이트
   */
  const updateItem = <T extends outbound.parcel.item, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundParcel/updatePackageItem', {
      key: key,
      value: value,
    });
  };
  /**
   * 요금정보 업데이트
   */
  const updateFee = <T extends outbound.parcel.fee, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundParcel/updatePackageFee', {
      key: key,
      value: value,
    });
  };
  /**
   * 주문정보 업데이트
   */
  const updateOrderInfomation = <T extends outbound.parcel.orderInfomation, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundParcel/updatePackageOrderInfomation', {
      key: key,
      value: value,
    });
  };
  /**
   * 주문완료목록 업데이트
   *
   * 배열초기화는 []를 넘겨줍니다.
   */
  const updateCompleteList = (value: outbound.parcel.package[]) => {
    store.dispatch('outboundParcel/updateCompleteList', cloneDeep([...value]));
  };

  /**
   * 출발지 정보 초기화
   */
  const resetOrigin = <T extends outbound.pin, K extends keyof T>() => {
    const key = Object.keys(MODEL_PIN()) as Array<K>;
    const value = Object.values(MODEL_PIN()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateOrigin(el, value[index]));
  };
  /**
   * 도착지 정보 초기화
   */
  const resetDestination = <T extends outbound.pin, K extends keyof T>() => {
    const key = Object.keys(MODEL_PIN()) as Array<K>;
    const value = Object.values(MODEL_PIN()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateDestination(el, value[index]));
  };
  /**
   * 물품 정보 초기화
   */
  const resetItem = <T extends outbound.parcel.item, K extends keyof T>() => {
    const key = Object.keys(MODEL_ITEM()) as Array<K>;
    const value = Object.values(MODEL_ITEM()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateItem(el, value[index]));
  };
  /**
   * 요금 정보 초기화
   */
  const resetFee = <T extends outbound.parcel.fee, K extends keyof T>() => {
    const key = Object.keys(MODEL_FEE()) as Array<K>;
    const value = Object.values(MODEL_FEE()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateFee(el, value[index]));
  };
  /**
   * 주문 정보 초기화
   */
  const resetOrderInfomation = <T extends outbound.parcel.orderInfomation, K extends keyof T>(type: string) => {
    const key = Object.keys(MODEL_ORDERINFOMATION()) as Array<K>;
    const value = Object.values(MODEL_ORDERINFOMATION()) as Array<T[K]>;

    key.forEach((el: K, index: number) => {
      if (type === 'all') {
        updateOrderInfomation(el, value[index]);
      } else {
        if (el === 'customFields') {
          const customFields = store.state.outboundParcel.packageOrderInfomation.customFields.map((el: any) => {
            return { ...el, value: '' };
          });
          //커스텀필드 초기값으로 업데이트
          updateOrderInfomation('customFields', customFields);
        } else if (el !== 'policyItem') updateOrderInfomation(el, value[index]);
      }
    });
  };

  /**
   * 현재 작성중인 주문을 초기화 합니다.
   *
   * 출발지도 초기화 시킬경우
   * @param true
   */
  const resetPackage = (isResetOrigin: boolean = false, type: string) => {
    resetDestination();
    resetItem();
    resetFee();
    resetOrderInfomation(type);
    isResetOrigin ? resetOrigin() : '';
    resetValidateInfo();
  };

  const resetValidateInfo = () => {
    store.dispatch('outboundParcel/resetValidateInfo');
  };

  //필수 입력 항목 체크 팝업 표시 여부 업데이트
  const updateValidatePopup = (value: boolean) => {
    return (store.state.outboundParcel.validatePopup = value);
  };

  //지점 운영시간 안내 팝업 표시 여부 업데이트
  const updateReceptionTimePopup = (value: boolean) => {
    return (store.state.outboundParcel.receptionTimePopup = value);
  };

  return {
    updateOrigin,
    updateDestination,
    updateItem,
    updateFee,
    updateOrderInfomation,
    updateCompleteList,
    resetOrigin,
    resetDestination,
    resetItem,
    resetFee,
    resetOrderInfomation,
    resetPackage,
    updateValidatePopup,
    updateReceptionTimePopup,
  };
}

/**
 *
 * 택배접수에 사용 되는 공통 action을 정의합니다.
 *
 */
export function useParcelAction() {
  const router = useRouter();
  const { chargeParcel } = useCharge();
  const { getClientInfo } = useClient();
  const { updateParcelPaymentList, updateCanUsePayment, getUsePaymentModalStatus } = usePaymentParcel();
  const { parcelCompleteList, parcelDestination, parcelItem } = useParcelState();
  const { updateFee, updateOrigin, resetFee, updateReceptionTimePopup } = useParcelUpdate();
  const { getCurrentOrder } = useParcelGetters();

  /**
   * 출발지 정보 로그인한 사용자 정보로 업데이트
   */
  const actionOriginUpdateUser = async () => {
    try {
      const { client, center } = await getClientInfo();
      updateOrigin('name', client['name']);
      updateOrigin('phoneNumber', {
        value: client['phoneNumber'],
        isValidate: true,
      });
      updateOrigin('dongName', center['dongName']);
      updateOrigin('jibunAddress', center['jibunAddress']);
      updateOrigin('roadAddress', center['roadAddress']);
      updateOrigin('detailAddress', center['detailAddress']);
      updateOrigin('latitude', center['latitude']);
      updateOrigin('longitude', center['longitude']);
      updateOrigin('postalCode', center['postalCode']);
    } catch {}
  };

  /**
   * 주문접수
   *
   */
  const actionSubmitOutbound = () => {
    const list = [...parcelCompleteList.value, getCurrentOrder.value];
    const pickupTime = dayjs().utc().format();

    // 택배 픽업시간설정
    list.forEach((el) => {
      el.origin.pickupTime = pickupTime;
      el.destination.pickupTime = pickupTime;
    });
    if(!list[0].customerDropOffMethod){
      list.forEach((el) => {
        el.customerDropOffMethod = list[list.length - 1].customerDropOffMethod;
      });
    }

    updateParcelPaymentList(list);

    // 택배 주문접수 컴포넌트 onUnMounted 시 주문 관련 모든 목록이 초기화 됩니다.
    updateCanUsePayment(true);
  };

  /**
   * 택배 요금을 계산합니다
   * 2023.05.10 parcelCategory(택배규격) 추가 (?v=2)
   */
  const actionCalculateFee = () => {
    const queue = new PQueue({ concurrency: 1 });

    watchEffect(async () => {
      const payload: api.charge.parcel.payload = {
        postalCode: parcelDestination.value.postalCode,
      };

      if (parcelItem.value.size._id) {
        payload.parcelCategoryId = parcelItem.value.size._id;
      }
      queue.clear();

      await queue.add(async () => {
        updateFee('totalCharge', null); // 로딩

        if (!parcelDestination.value.postalCode || !parcelItem.value.size._id) return resetFee();

        try {
          const { chargeInfo } = await chargeParcel(payload);

          const key = Object.keys(chargeInfo);
          key.forEach((el) => {
            updateFee(el, chargeInfo[el]);
          });
        } catch (e) {
          resetFee();
        }
      });
    });
  };

  const actionReceptionTimePopup = (value: boolean) => {
    updateReceptionTimePopup(value);
  };

  return {
    actionSubmitOutbound,
    actionOriginUpdateUser,
    actionCalculateFee,
    actionReceptionTimePopup,
  };
}

/**
 * 택배관련 getters
 */
export function useParcelGetters() {
  const { parcelCompleteList, parcelFee, parcelOrderInfomation, parcelOrigin, parcelDestination, parcelItem } =
    useParcelState();

  /**
   * 현재 작성중인 주문
   */
  const getCurrentOrder = computed<outbound.parcel.package>(() => {
    return {
      origin: parcelOrigin.value,
      destination: parcelDestination.value,
      item: parcelItem.value,
      fee: parcelFee.value,
      orderInfomation: parcelOrderInfomation.value,
      customerDropOffMethod: parcelOrderInfomation.value.customerDropOffMethod,
      onsitePickupRequestedAt: parcelOrderInfomation.value.onsitePickupRequestedAt,
      onsitePickupClientComment: parcelOrderInfomation.value.onsitePickupClientComment,
    };
  });

  /**
   * 모든 택배의 총 금액
   */
  const getTotalFee = computed<Readonly<number>>(() => {
    // 완료주문의 총 금액
    const completeFee = parcelCompleteList.value.reduce((stack, el, index) => {
      const elFee = el.fee.totalCharge!;
      return stack + elFee;
    }, 0);
    // 현재 주문의 금액
    const currentFee = parcelFee.value.totalCharge!;
    return completeFee + currentFee;
  });

  /**
   *  접수에 필요한 주문정보가 모두 있는지 확인
   *
   *  false: 접수불가, true: 접수가능
   */
  const getCompleteCondition = computed<Readonly<boolean>>(() => {
    const _conditionPin = (pin: outbound.pin) => {
      if (!pin.phoneNumber.isValidate || pin.phoneNumber.value === '') return false; // 핸드폰번호 검증 안된경우

      const checkKey = ['name', 'phoneNumber', 'postalCode', 'detailAddress'];
      let checkEmptyValue = true;

      checkKey.forEach((element) => {
        if (!pin[element]) checkEmptyValue = false;
      });

      return checkEmptyValue;
    };

    const conditionFee = parcelFee.value.chargeUuid ? true : false;
    const conditionOrigin = _conditionPin(parcelOrigin.value);
    const conditionDestination = _conditionPin(parcelDestination.value);
    const conditionItem =
      !parcelItem.value.category || !parcelItem.value.price || !parcelItem.value.size ? false : true;
    const conditionCustomFields = () => {
      let isPass = false;

      const customArray = parcelOrderInfomation.value.customFields!;
      if (customArray.length)
        isPass = customArray.every((el: any) => {
          return el.value;
        });
      else isPass = true;

      return isPass;
    };

    const isSamePinAddress = (origin: outbound.pin, destination: outbound.pin) => {
      if (origin.jibunAddress === destination.jibunAddress) return true;

      if (origin.roadAddress === destination.roadAddress) return true;

      if (origin.latitude === destination.latitude && origin.longitude === destination.longitude) return true;

      return false;
    };

    return !conditionOrigin ||
      !conditionDestination ||
      !conditionItem ||
      !conditionFee ||
      !conditionCustomFields() ||
      isSamePinAddress(parcelOrigin.value, parcelDestination.value)
      ? false
      : true;
  });

  /**
   * 주문접수 가능 한 상태인지 확인
   *
   * 접수가능(true), 접수 불가능(false)
   */
  const getSubmitCondition = computed<Readonly<boolean>>(() => {
    const conditionPolicy = parcelOrderInfomation.value.policyItem ? true : false; // 물품약관 동의

    return !conditionPolicy || !getCompleteCondition.value ? false : true;
  });

  /**
   * 완료주문 + 현재 작성 중인 주문
   */
  const getAllOrders = computed<Readonly<outbound.parcel.package[]>>(() => {
    return [...parcelCompleteList.value, getCurrentOrder.value];
  });

  return {
    getTotalFee,
    getSubmitCondition,
    getCompleteCondition,
    getAllOrders,
    getCurrentOrder,
  };
}
